import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { StyledValueDetails, StyledValueDetailsHeader } from '../styles';
import { useLicenceAuthorities } from '../hooks';
import { Loader } from 'components/atoms/Loader';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import PDFViewer from 'components/molecules/PDFViewer';
import { EMIMETypesLabel } from 'constants/MIMETypes';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { colors } from 'theme/colors';
import { CardList, emptyFieldHolder } from 'components/molecules/CardList';
import { CopyableTruncatedField } from '../../../components/molecules/CopyableTruncatedField';
import { EIconTypes } from '../../../constants/Icons';
import { Table } from 'components/molecules/Table';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import {
  licenceAuthorityPrintoutErrorLogColumns,
  mapLicenceAuthorityPrintoutErrorLogDataToDataSource,
} from 'pages/LicenceAuthoritiesPage/helpers';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages } from 'models/table';
import { LicenceAuthorityPrintoutErrorLogTableCard } from 'pages/LicenceAuthoritiesPage/LicenceAuthorityPage/LicenceAuthorityPrintoutErrorLogTableCard';
import {
  ELicenceAuthorityPrintoutErrorLogStatus,
  ELicenceAuthorityPrintoutErrorLogType,
  ILicenceAuthorityPrintoutErrorLog,
} from 'models/licenceAuthority';
import { clearAlertTimeout, EAlertTime } from 'utils/date';

const LicenceAuthorityPage = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { nextPageAlert, samePageAlert, clearAlert, printoutErrorLogAlert } = useAlert();
  const { id = '' } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAddSettings, setIsLoadingAddSettings] = useState(false);
  const { isDesktop } = useThemeBreakpoint();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const [currentPagePrintoutErrorLog, setCurrentPagePrintoutErrorLog] = useState<number>(1);
  const [perPagePrintoutErrorLog, setPerPagePrintoutErrorLog] = useState<EPerPages>(
    EPerPages.perPage25,
  );
  const [isLoadingPrintoutErrorLog, setIsLoadingPrintoutErrorLog] = useState<boolean>(true);

  const {
    getLicenceAuthorityData,
    licenceAuthority,
    addPrintoutSettings,
    printoutSettings,
    getPrintoutSettings,
    getPrintoutErrorLogData,
    printoutErrorLogData,
    paginatorPrintoutErrorLog,
    resumeSending,
    resolvePrintoutErrorLog,
    getLicenceAuthoritySftpSendStateData,
    printoutCanBeResumed,
  } = useLicenceAuthorities();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_APPLICATION_PROCESS_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => {
      clearAlertTimeout(
        clearAlert,
        ActionTypes.CLEAR_APPLICATION_PROCESS_ALERT,
        EAlertTime.LONG,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      getLicenceAuthorityData(id);
      getPrintoutSettings(id);
      setIsLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      setIsLoadingPrintoutErrorLog(true);
      await getPrintoutErrorLogData(id, currentPagePrintoutErrorLog, perPagePrintoutErrorLog);
      setIsLoadingPrintoutErrorLog(false);
    })();
  }, [id, currentPagePrintoutErrorLog, perPagePrintoutErrorLog]);

  useEffect(() => {
    (async () => {
      await getLicenceAuthoritySftpSendStateData(id);
    })();
  }, [id]);

  const handleAddSettings = async () => {
    setIsLoadingAddSettings(true);
    try {
      await addPrintoutSettings(id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingAddSettings(false);
    }
  };

  const handleDetailsPrintoutSettings = () => {
    navigate(ERouteLinks.DetailsLicenceAuthoritiesPrintoutSettings.replace(':id', id));
  };
  const handleAddPrintoutSettings = () => {
    navigate(ERouteLinks.AddLicenceAuthoritiesPrintoutSettings.replace(':id', id));
  };
  const handleEdit = () => navigate(ERouteLinks.EditLicenceAuthorities.replace(':id', id));

  const handleResumeSending = async () => {
    const res = await resumeSending(id);
    if (res) {
      getPrintoutSettings(id);
      getPrintoutErrorLogData(id, currentPagePrintoutErrorLog, perPagePrintoutErrorLog);
      getLicenceAuthoritySftpSendStateData(id);
    }
  };
  const printoutErrorResolveAction = async (uniqueId: string) => {
    const res = await resolvePrintoutErrorLog(id, uniqueId);
    if (res) {
      getPrintoutErrorLogData(id, currentPagePrintoutErrorLog, perPagePrintoutErrorLog);
    }
  };

  const renderSignaturePreview = () => {
    const url = licenceAuthority?.signatureAttachmentUrl;

    if (!url) return emptyFieldHolder(url);

    if (licenceAuthority?.signatureAttachmentType === EMIMETypesLabel.PDF)
      return <PDFViewer pdfUrl={url} height={320} />;

    return (
      <img
        src={url}
        alt="file"
        style={{ width: 'auto', maxWidth: '100%', maxHeight: '320px', objectFit: 'cover' }}
      />
    );
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          { label: t('licenceAuthorities.header'), route: ERouteLinks.LicenceAuthorities },
          t('licenceAuthorities.detailsLicenceAuthority.header'),
        ]}
      />
      <Grid
        container
        sx={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', mb: 1 }}
      >
        <StyledPageTitle variant="h4">
          {t('licenceAuthorities.detailsLicenceAuthority.header')}
        </StyledPageTitle>
        <Grid>
          <Button
            label={t('general.edit.button')}
            variant={EButtonVariants.contained}
            onClick={handleEdit}
          />
        </Grid>
      </Grid>

      {nextPageAlert && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
        </Box>
      )}
      {samePageAlert && (
        <Grid size={{ xs: 12 }}>
          <Box sx={{ width: '100%', my: 2 }}>
            <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
          </Box>
        </Grid>
      )}
      {isLoading ? (
        <Loader isLoading={true} />
      ) : (
        <Grid container sx={{ gap: 3 }}>
          <Grid size={{ xs: 12 }}>
            <StyledValueDetailsHeader>
              {t('licenceAuthorities.generalInformation.label')}
            </StyledValueDetailsHeader>
          </Grid>
          <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licenceAuthorities.licenceAuthorityName.label')}
              </Typography>
              <Typography variant="body2">{licenceAuthority?.name}</Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licenceAuthorities.status.label')}
              </Typography>
              <Chips
                text={
                  licenceAuthority?.isActive
                    ? t('general.active.label')
                    : t('general.inactive.label')
                }
                variant={
                  licenceAuthority?.isActive ? EChipsVariants.success : EChipsVariants.error
                }
              />
            </Grid>
          </StyledValueDetails>
          <Grid size={{ xs: 12 }}>
            <StyledValueDetailsHeader>
              {t('licenceAuthorities.licenceAuthoritySignature.label')}
            </StyledValueDetailsHeader>
          </Grid>
          <Grid container size={{ xs: 12 }} columnSpacing="10px">
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licenceAuthorities.licenceAuthoritySignaturePreview.label')}
              </Typography>
              <Typography variant="body2">{renderSignaturePreview()}</Typography>
            </Grid>
          </Grid>
          <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licenceAuthorities.authorizedPersonName.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licenceAuthority?.authorizedPersonName)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
                {t('licenceAuthorities.position.label')}
              </Typography>
              <Typography variant="body2">
                {emptyFieldHolder(licenceAuthority?.position)}
              </Typography>
            </Grid>
          </StyledValueDetails>

          <Grid size={{ xs: 12 }}>
            <StyledValueDetailsHeader
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              {t('licenceAuthorities.printoutSettings.label')}
              {printoutSettings ? (
                <>
                  {printoutSettings.version !== 0 ? (
                    <Button
                      label={t('general.details.text')}
                      variant={EButtonVariants.outlined}
                      onClick={handleDetailsPrintoutSettings}
                      size={EButtonSizes.small}
                      sx={{ mt: '-4px' }}
                    />
                  ) : (
                    <Button
                      label={t('general.addSettings.button')}
                      variant={EButtonVariants.outlined}
                      onClick={handleAddPrintoutSettings}
                      size={EButtonSizes.small}
                      sx={{ mt: '-4px' }}
                    />
                  )}
                </>
              ) : (
                <Button
                  label={t('licenceAuthoritiesPrintoutSettings.initialize.text')}
                  variant={EButtonVariants.outlined}
                  onClick={handleAddSettings}
                  size={EButtonSizes.small}
                  sx={{ mt: '-4px' }}
                  disabled={isLoadingAddSettings}
                />
              )}
            </StyledValueDetailsHeader>
          </Grid>
          <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
            {isLoadingAddSettings && (
              <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
                <Loader isLoading />
              </Grid>
            )}
            {!isLoadingAddSettings && printoutSettings ? (
              <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
                <Grid size={{ xs: 12, md: 6 }} sx={{ p: 2 }}>
                  <CopyableTruncatedField
                    label={t('licenceAuthoritiesPrintoutSettings.publicKey.label')}
                    value={emptyFieldHolder(printoutSettings?.sshOwnPublicKey)}
                    maxLength={30}
                    buttonIcon={EIconTypes.copy}
                    buttonVariant={EButtonVariants.outlined}
                    buttonSize={EButtonSizes.small}
                    buttonSizePX={EButtonSizesPX.px40}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }} sx={{ p: 2 }}>
                  <CopyableTruncatedField
                    label={t('licenceAuthoritiesPrintoutSettings.keyFirecycleSystem.label')}
                    value={emptyFieldHolder(printoutSettings?.pgpOwnPublicKey)}
                    maxLength={30}
                    buttonIcon={EIconTypes.copy}
                    buttonVariant={EButtonVariants.outlined}
                    buttonSize={EButtonSizes.small}
                    buttonSizePX={EButtonSizesPX.px40}
                  />
                </Grid>
              </StyledValueDetails>
            ) : (
              <Grid container size={{ xs: 12 }} sx={{ justifyContent: 'center' }}>
                <Typography
                  style={{ fontStyle: 'italic', color: colors.gray400 }}
                  variant="body2"
                >
                  {t('licenceAuthorities.printoutSettingsNotDefined.label')}
                </Typography>
              </Grid>
            )}
          </StyledValueDetails>
          <Grid size={{ xs: 12 }}>
            <StyledValueDetailsHeader
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              {t('licenceAuthorities.printoutErrorLog.label')}
              {printoutCanBeResumed && (
                <Button
                  label={t('licenceAuthorities.printoutErrorLog.resume.button')}
                  variant={EButtonVariants.outlined}
                  onClick={handleResumeSending}
                  size={EButtonSizes.small}
                  sx={{ mt: '-4px' }}
                />
              )}
            </StyledValueDetailsHeader>
            {printoutErrorLogAlert && (
              <Box sx={{ width: '100%', mt: 1, ml: '-5px' }}>
                <Alert
                  text={printoutErrorLogAlert.text}
                  variant={printoutErrorLogAlert.variant}
                />
              </Box>
            )}
            <Box sx={{ mt: 3 }}>
              {isDesktop ? (
                <Table
                  columns={licenceAuthorityPrintoutErrorLogColumns({
                    longDateFormat,
                    t,
                    resolveAction: printoutErrorResolveAction,
                  })}
                  loading={isLoadingPrintoutErrorLog}
                  dataSource={mapLicenceAuthorityPrintoutErrorLogDataToDataSource(
                    printoutErrorLogData ?? [],
                  )}
                />
              ) : (
                <CardList
                  items={mapLicenceAuthorityPrintoutErrorLogDataToDataSource(
                    printoutErrorLogData ?? [],
                  )}
                  loading={isLoadingPrintoutErrorLog}
                  render={(data, index) => (
                    <LicenceAuthorityPrintoutErrorLogTableCard
                      data={data}
                      longDateFormat={longDateFormat}
                      resolveAction={printoutErrorResolveAction}
                      key={index}
                    />
                  )}
                />
              )}
              {paginatorPrintoutErrorLog && (
                <Pagination
                  count={paginatorPrintoutErrorLog?.totalElements}
                  onChangePage={(page) => setCurrentPagePrintoutErrorLog(page)}
                  current={currentPagePrintoutErrorLog}
                  perPage={perPagePrintoutErrorLog}
                  onChangePerPage={(value) => setPerPagePrintoutErrorLog(value)}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default LicenceAuthorityPage;
